import { PaymentMethod, PaymentOption, PaymentType } from '../../types/types';
import { ParticipantPayment, SlotService } from '../state/types';
import { patchParticipant } from './participants';

/**
 * Participant 1 (the booker) pays with the slot's own `selectedPaymentOption`, exactly like
 * a single booking. Participants after the first keep their own choice in
 * `participants[index].payment`, set to their payment section's default when they are added
 * (see withDefaultParticipantPayments). A participant without one, when no default exists,
 * falls back to the slot selection so pricing/summary always have a payment option.
 */
export const isOwnPaymentParticipant = (
  participantIndex?: number,
): participantIndex is number =>
  participantIndex !== undefined && participantIndex > 0;

export const getParticipantPayment = (
  slotService: SlotService,
  participantIndex?: number,
): ParticipantPayment => {
  const own = isOwnPaymentParticipant(participantIndex)
    ? slotService.participants?.[participantIndex]?.payment
    : undefined;
  return (
    own ?? {
      selectedPaymentOption: slotService.selectedPaymentOption,
      selectedPaymentType: slotService.selectedPaymentType,
    }
  );
};

export const findSelectedMembership = (
  slotService: SlotService,
  participantIndex?: number,
) => {
  const { selectedPaymentOption } = getParticipantPayment(
    slotService,
    participantIndex,
  );
  return slotService.memberships?.eligibleMemberships?.find(
    (membership) => membership?.id === selectedPaymentOption.id,
  );
};

export const withParticipantPayment = (
  participants: SlotService['participants'],
  participantIndex: number,
  payment: ParticipantPayment,
): NonNullable<SlotService['participants']> =>
  patchParticipant(participants, participantIndex, { payment });

const PLAN_OPTION_TYPES = new Set<PaymentType>([
  PaymentType.USE_PLAN,
  PaymentType.BUY_PLAN,
  PaymentType.BUY_PLAN_AND_BOOK,
]);

/**
 * Any pricing-plan based payment: using an owned plan (type MEMBERSHIP) as well as the
 * buy-a-plan options, which keep a non-membership payment type until the plan is bought.
 */
export const isPlanPayment = (payment?: {
  selectedPaymentType?: PaymentMethod;
  selectedPaymentOption?: PaymentOption;
}): boolean =>
  payment?.selectedPaymentType === PaymentMethod.MEMBERSHIP ||
  (!!payment?.selectedPaymentOption?.type &&
    PLAN_OPTION_TYPES.has(payment.selectedPaymentOption.type));

export const isBuyingPlan = (payment?: {
  selectedPaymentOption?: PaymentOption;
}): boolean =>
  payment?.selectedPaymentOption?.type === PaymentType.BUY_PLAN ||
  payment?.selectedPaymentOption?.type === PaymentType.BUY_PLAN_AND_BOOK;

export const toFinitePrice = (value?: string | null): number | undefined => {
  const price = value?.trim() ? Number(value) : NaN;
  return Number.isFinite(price) ? price : undefined;
};

export const getBasePrice = ({
  service,
  dynamicPriceInfo,
}: Pick<SlotService, 'service' | 'dynamicPriceInfo'>): number =>
  toFinitePrice(dynamicPriceInfo?.defaultPrice?.amount) ??
  service.payment.paymentDetails.price;
